import React, { useEffect, useMemo } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  logout as userLogout,
  fetchUserNotifications,
  markUserNotificationRead,
} from "../../store/userSlice";
import { logout as hospitalLogout } from "../../store/hospitalSlice";
import { useLanguage } from "../../context/LanguageContext";

const formatTimeAgo = (value) => {
  if (!value) return "Just now";
  const ms = Date.now() - new Date(value).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr > 1 ? "s" : ""} ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day > 1 ? "s" : ""} ago`;
};

function Header() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.user.userInfo);
  const hospitalInfo = useSelector((state) => state.hospital.hospitalInfo);
  const notifications = useSelector((state) => state.user.notifications);
  const unreadNotifications = useSelector((state) => state.user.unreadNotifications);
  const { t, languages, selectedLanguage, setSelectedLanguage } = useLanguage();
  const languageFlagMap = {
    en: "/assets/images/icon/flag-eng.svg",
    de: "/assets/images/icon/flag-germeny.svg",
    fr: "/assets/images/icon/flag-french.svg",
    bn: "/assets/images/icon/flag-bangla.svg",
    es: "/assets/images/icon/flag-french.svg",
    af: "/assets/images/icon/flag-eng.svg",
    it: "/assets/images/icon/flag-french.svg",
    pt: "/assets/images/icon/flag-french.svg",
  };

  const isLoggedIn = Boolean(userInfo || hospitalInfo);
  const dashboardPath = useMemo(() => {
    if (hospitalInfo) return "/hospital-dashboard";
    if (userInfo?.userDocument?.isAdmin) return "/admin-dashboard";
    if (userInfo) return "/user-dashboard";
    return "/login";
  }, [hospitalInfo, userInfo]);

  useEffect(() => {
    if (userInfo) {
      dispatch(fetchUserNotifications());
    }
  }, [dispatch, userInfo]);

  const handleLogout = () => {
    if (userInfo) dispatch(userLogout());
    if (hospitalInfo) dispatch(hospitalLogout());
    localStorage.removeItem("UserInfo");
    localStorage.removeItem("HospitalInfo");
    navigate("/");
  };

  const handleNotificationClick = (id, isRead) => {
    if (!isRead) {
      dispatch(markUserNotificationRead(id));
    }
  };

  return (
    <header className="header-area style-1">
      <div className="header-main-logo d-lg-block d-none">
        <Link to="/">
          <img
            alt="Health Professional Workers Pool"
            src="/assets/images/hpwp-logo.jpg"
            style={{ maxHeight: 64, width: 220, objectFit: "contain" }}
          />
        </Link>
      </div>
      <div className="menu-topbar-area">
        <div className="top-bar">
          <p>
            {t("welcome")} <Link to="/bookmark">{t("saveJobs")}</Link>
          </p>
          <div className="top-bar-right">
            <div className="language-select">
              <img src={languageFlagMap[selectedLanguage] || "/assets/images/icon/flag-eng.svg"} alt="flag" />
              <span>{t("language")}</span>
              <ul className="topbar-sublist">
                {languages.map((lang) => (
                  <li key={lang.code}>
                    <button
                      type="button"
                      onClick={() => setSelectedLanguage(lang.code)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                      }}
                    >
                      <img src={languageFlagMap[lang.code] || "/assets/images/icon/flag-eng.svg"} alt="flag" />
                      <span>{lang.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="social-area">
              <ul>
                <li>
                  <a href="https://www.facebook.com/" target="_blank" rel="noreferrer">
                    <i className="bx bxl-facebook" />
                  </a>
                </li>
                <li>
                  <a href="https://twitter.com/" target="_blank" rel="noreferrer">
                    <i className="bx bxl-twitter" />
                  </a>
                </li>
                <li>
                  <a href="https://www.linkedin.com/" target="_blank" rel="noreferrer">
                    <i className="bx bxl-linkedin" />
                  </a>
                </li>
                <li>
                  <a href="https://www.instagram.com/" target="_blank" rel="noreferrer">
                    <i className="bx bxl-instagram" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="menu-area">
          <div className="header-logo">
            <Link to="/">
              <img
                alt="Health Professional Workers Pool"
                className="img-fluid"
                src="/assets/images/hpwp-logo.jpg"
                style={{ maxHeight: 56, width: 210, objectFit: "contain" }}
              />
            </Link>
          </div>
          <div className="main-menu">
            <ul className="menu-list">
              <li>
                <NavLink to="/" end className={({ isActive }) => (isActive ? "active" : undefined)}>
                  {t("home")}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/find-job"
                  className={({ isActive }) => (isActive ? "active" : undefined)}
                >
                  {t("findJobs")}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/about"
                  className={({ isActive }) => (isActive ? "active" : undefined)}
                >
                  {t("aboutUs")}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/blog"
                  className={({ isActive }) => (isActive ? "active" : undefined)}
                >
                  {t("blog")}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/contact"
                  className={({ isActive }) => (isActive ? "active" : undefined)}
                >
                  {t("contact")}
                </NavLink>
              </li>
            </ul>

            <div className="for-mobile-menu d-lg-none d-block">
              <div className="sign-in-btn mb-25">
                {isLoggedIn ? (
                  <Link className="primry-btn-1 lg-btn" to={dashboardPath}>
                    {t("dashboard")}
                  </Link>
                ) : (
                  <Link className="primry-btn-1 lg-btn" to="/login">
                    {t("signIn")}
                  </Link>
                )}
              </div>
            </div>
          </div>

          <div className="nav-right d-flex jsutify-content-end align-items-center">
            <ul>
              <li>
                <div className="btn-group dropdown">
                  <div
                    className="notifications-area dropdown-toggle"
                    role="button"
                    id="dropdownMenuButton2"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <div className="notifacation-icon">
                      <svg
                        width={16}
                        height={18}
                        viewBox="0 0 16 18"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M8.37408 0.0465755C8.67279 0.120485 8.89683 0.247189 9.12442 0.472435C9.4907 0.834944 9.60093 1.15874 9.60093 1.89079V2.33777L9.87831 2.43983C11.5497 3.05223 12.8263 4.40723 13.3028 6.08603C13.47 6.66323 13.4806 6.81809 13.5126 8.47929C13.5446 10.2109 13.5624 10.4326 13.7651 11.2597C13.9856 12.1501 14.3874 13.0546 14.9102 13.836C15.1378 14.1773 15.344 14.4378 15.8739 15.0431C16.0872 15.2895 16.009 15.6872 15.7174 15.835C15.5823 15.9019 15.5147 15.9054 13.093 15.9054H10.6038L10.5824 16.0251C10.49 16.5143 10.0561 17.1478 9.56537 17.5068C9.34845 17.6652 8.95728 17.8517 8.67635 17.9327C8.38831 18.0136 7.68776 18.0242 7.39616 17.9502C6.50002 17.7285 5.7568 17.0528 5.48654 16.2187L5.38696 15.9054H2.90481C0.490226 15.9054 0.419104 15.9019 0.283973 15.835C0.191514 15.7893 0.116836 15.7154 0.0706072 15.6239C-0.0645256 15.3634 -0.0111828 15.1769 0.280416 14.8672C1.18722 13.9063 1.90911 12.5795 2.23627 11.2597C2.43896 10.4432 2.4603 10.2144 2.48875 8.47929C2.52075 6.81457 2.53142 6.66675 2.69856 6.08251C3.17152 4.41075 4.50861 2.99943 6.15864 2.42224L6.40045 2.33777V1.89079C6.40045 1.16226 6.51069 0.834944 6.88052 0.468916C7.27881 0.0712128 7.83711 -0.0871639 8.37408 0.0465755Z" />
                      </svg>
                    </div>
                    <span>{userInfo ? unreadNotifications : 0}</span>
                  </div>
                  <div className="notifacion-card dropdown-menu" aria-labelledby="dropdownMenuButton2">
                    <h6 className="title">
                      {userInfo ? `${notifications.length} Notifications` : "No Notifications"}
                    </h6>
                    <ul>
                      {!userInfo || notifications.length === 0 ? (
                        <li>
                          <div className="content">
                            <h6>No notifications available.</h6>
                          </div>
                        </li>
                      ) : (
                        notifications.slice(0, 5).map((item) => (
                          <li key={item._id}>
                            <div className="content">
                              <h6>
                                <button
                                  onClick={() => handleNotificationClick(item._id, item.isRead)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                    font: "inherit",
                                    textDecoration: item.isRead ? "none" : "underline",
                                    color: "inherit",
                                    textAlign: "left",
                                  }}
                                >
                                  {item.title}
                                </button>
                              </h6>
                              <p>{item.message}</p>
                              <span>
                                <img src="/assets/images/icon/clock-1.svg" alt="" />{" "}
                                {formatTimeAgo(item.createdAt)}
                              </span>
                            </div>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                </div>
              </li>
              <li className="d-md-flex d-none">
                <div className="sign-in-btn">
                  {isLoggedIn ? (
                    <Link className="primry-btn-1 lg-btn" to={dashboardPath}>
                      {t("dashboard")}
                    </Link>
                  ) : (
                    <Link className="primry-btn-1 lg-btn" to="/login">
                      {t("signIn")}
                    </Link>
                  )}
                </div>
              </li>
              {isLoggedIn && (
                <li className="d-md-flex d-none">
                  <div className="post-job-btn">
                    <button className="primry-btn-2 lg-btn" onClick={handleLogout}>
                      {t("logout")}
                    </button>
                  </div>
                </li>
              )}
            </ul>
            <div className="sidebar-button mobile-menu-btn">
              <i className="bi bi-list" />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
